import Compressor from "compressorjs";
import { MediaQualityOptions } from "@jugl-web/rest-api";

const isBrowserCompatible = (file: File): Promise<boolean> =>
  new Promise((resolve) => {
    if (!file.type.startsWith("image/")) {
      resolve(false);
    }

    const hasRequiredAPIs =
      typeof File !== "undefined" &&
      typeof FileReader !== "undefined" &&
      typeof URL !== "undefined" &&
      typeof URL.createObjectURL === "function" &&
      typeof HTMLCanvasElement !== "undefined";

    if (!hasRequiredAPIs) {
      resolve(false);
    }

    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    const timeout = setTimeout(() => {
      URL.revokeObjectURL(objectUrl);
      resolve(false);
    }, 1000);

    img.onload = () => {
      img.decode().then(
        () => {
          clearTimeout(timeout);
          URL.revokeObjectURL(objectUrl);
          resolve(true);
        },
        () => {
          clearTimeout(timeout);
          URL.revokeObjectURL(objectUrl);
          resolve(false);
        }
      );
    };

    img.onerror = () => {
      clearTimeout(timeout);
      URL.revokeObjectURL(objectUrl);
      resolve(false);
    };

    img.src = objectUrl;
  });

const imageCompressionSettings = {
  compressed: {
    imageQuality: 0.7,
    targetSize: 1 * 1024 * 1024,
    maxSize: 512,
  },
  good: {
    imageQuality: 0.8,
    targetSize: 2 * 1024 * 1024,
    maxSize: 1024,
  },
  excellent: {
    imageQuality: 0.9,
    targetSize: 4 * 1024 * 1024,
    maxSize: 2048,
  },
  uncompressed: {
    imageQuality: 1,
    targetSize: 75 * 1024 * 1024,
    maxSize: 4096,
  },
};
export const compressImage = async (
  file: File,
  quality: MediaQualityOptions
) => {
  const imageTargetSize = imageCompressionSettings[quality].targetSize;
  const isLessThanTargetSize = file.size < imageTargetSize;

  const isImageSupported = await isBrowserCompatible(file);

  if (quality === "uncompressed" || isLessThanTargetSize || !isImageSupported) {
    return Promise.resolve(file);
  }

  return new Promise<File>((resolve, reject) => {
    Reflect.construct(Compressor, [
      file,
      {
        maxWidth: imageCompressionSettings[quality].maxSize,
        maxHeight: imageCompressionSettings[quality].maxSize,
        quality: imageCompressionSettings[quality].imageQuality,
        success: (compressedFile: File | Blob) => {
          let blobAsFile = null;
          if (compressedFile instanceof Blob) {
            blobAsFile = new File([compressedFile], file.name, {
              type: compressedFile.type,
            });
          }
          resolve(blobAsFile ?? (compressedFile as File));
        },
        error: (error: Error) => {
          reject(error);
        },
      },
    ]);
  });
};
